import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes } from 'react-icons/fa';
import { ScheduleCommunityModel } from '../../../../models/schedule.model';
import Button from '../../../components/buttons/Button';
import Input from '../../../components/misc/Input';

export type DraftShiftCommunityAssignment = {
  communityId: number;
  communityName: string;
  requiredCount: number;
  assignedCount?: number;
};

type ShiftCommunityAssignmentsEditorProps = {
  linkedCommunities: ScheduleCommunityModel[];
  assignments: DraftShiftCommunityAssignment[];
  onChange: (assignments: DraftShiftCommunityAssignment[]) => void;
};

export default function ShiftCommunityAssignmentsEditor({
  linkedCommunities,
  assignments,
  onChange,
}: ShiftCommunityAssignmentsEditorProps): JSX.Element | null {
  const { t } = useTranslation();

  if (linkedCommunities.length === 0) {
    return (
      <p className="rounded-xl bg-primary-100 px-3 py-2 text-sm text-primary-700">
        {t('page.event.schedules.communities.linkFirst')}
      </p>
    );
  }

  const availableToAdd = linkedCommunities.filter(
    (community) => !assignments.some((assignment) => assignment.communityId === community.communityId),
  );

  const addAssignment = (communityId: number) => {
    const community = linkedCommunities.find((entry) => entry.communityId === communityId);
    if (!community) return;

    onChange([
      ...assignments,
      {
        communityId: community.communityId,
        communityName: community.name,
        requiredCount: 1,
        assignedCount: 0,
      },
    ]);
  };

  const updateRequiredCount = (communityId: number, value: string) => {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
      return;
    }

    onChange(
      assignments.map((assignment) =>
        assignment.communityId === communityId
          ? { ...assignment, requiredCount: parsed }
          : assignment,
      ),
    );
  };

  const removeAssignment = (communityId: number) => {
    onChange(assignments.filter((assignment) => assignment.communityId !== communityId));
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-secondary-200 bg-secondary-100 p-3">
      <div>
        <p className="font-medium text-primary-900">
          {t('page.event.schedules.communities.shiftAssignmentsTitle')}
        </p>
        <p className="text-xs text-primary-500">
          {t('page.event.schedules.communities.shiftAssignmentsSubtitle')}
        </p>
      </div>

      {assignments.length === 0 ? (
        <p className="text-sm text-primary-500">
          {t('page.event.schedules.communities.noShiftAssignments')}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {assignments.map((assignment) => (
            <div
              key={assignment.communityId}
              className="flex flex-wrap items-center gap-3 rounded-lg bg-secondary-50 px-3 py-2">
              <div className="min-w-[140px] flex-1">
                <p className="text-sm font-medium text-primary-900">{assignment.communityName}</p>
                {typeof assignment.assignedCount === 'number' && (
                  <p className="text-xs text-primary-500">
                    {t('page.event.schedules.communities.fillStatus', {
                      assigned: assignment.assignedCount,
                      required: assignment.requiredCount,
                    })}
                  </p>
                )}
              </div>
              <div className="w-28">
                <Input
                  id={`community-required-${assignment.communityId}`}
                  label={t('page.event.schedules.communities.requiredPeople')}
                  type="number"
                  min={1}
                  value={String(assignment.requiredCount)}
                  onChange={(event) => updateRequiredCount(assignment.communityId, event.target.value)}
                />
              </div>
              <button
                type="button"
                onClick={() => removeAssignment(assignment.communityId)}
                className="text-primary-500 transition-colors hover:text-primary-900">
                <FaTimes className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {availableToAdd.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {availableToAdd.map((community) => (
            <Button
              key={community.communityId}
              variant="secondary"
              onClick={() => addAssignment(community.communityId)}>
              {t('page.event.schedules.communities.addCommunity', { name: community.name })}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

export function getCommunityAssignmentsPayload(
  assignments: DraftShiftCommunityAssignment[],
): { communityId: number; requiredCount: number }[] {
  return assignments.map((assignment) => ({
    communityId: assignment.communityId,
    requiredCount: assignment.requiredCount,
  }));
}

export function getTotalCommunityRequiredCount(assignments: DraftShiftCommunityAssignment[]): number {
  return assignments.reduce((total, assignment) => total + assignment.requiredCount, 0);
}

export function mapShiftCommunityAssignments(
  assignments: {
    communityId: number;
    communityName: string;
    requiredCount: number;
    assignedCount: number;
  }[],
): DraftShiftCommunityAssignment[] {
  return assignments.map((assignment) => ({
    communityId: assignment.communityId,
    communityName: assignment.communityName,
    requiredCount: assignment.requiredCount,
    assignedCount: assignment.assignedCount,
  }));
}
